import React from 'react';
import { AuthContextProvider } from './AuthContext';
import { ChakraProvider } from '@chakra-ui/react';
import theme from './assets/themes/theme.ts';
import { lazy, Suspense } from 'react';
import { LoadScript } from '@react-google-maps/api';
import { Routes, Route } from 'react-router-dom';
import Fallback from './components/Fallback/Fallback';
import PageLayout from './components/Layout/PageLayout';
import LandingPageLayout from './components/Layout/LandingPageLayout';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import LiveClockPage from './views/LiveClockPage/LiveClockPage';
import DealerCertification from './views/DealerCertification/DealerCertification';
import DealerPassportPage from './views/DealerPassportPage/DealerPassportPage';
import DealerDiscoveryPage from './views/DealerDiscoveryPage/DealerDiscoveryPage';
import SubmitGamePage from './views/SubmitGamePage/SubmitGamePage';
import FindAGamePage from './views/FindAGamePage/FindAGamePage';
import VenueProfilePage from './views/VenueProfilePage/VenueProfilePage';
import CreateVenuePage from './views/CreateVenuePage/CreateVenuePage';
import PCTDSApplicationPage from './views/PCTDSApplicationPage/PCTDSApplicationPage';
import TDRoomPage from './views/TDRoomPage/TDRoomPage';
import PosterGeneratorPage from './views/PosterGeneratorPage/PosterGeneratorPage';
import PCTDSClockPage from './views/PCTDSClockPage/PCTDSClockPage';
import TDControlCenter from './views/TDControlCenter/TDControlCenter';

const LandingPage = lazy(() => import('./views/LandingPage/LandingPage.tsx'));
const ComingSoonPage = lazy(
  () => import('./views/ComingSoonPage/ComingSoonPage.tsx')
);

const DashboardPage = lazy(() => import('./views/Dashboard/Dashboard.tsx'));
const CreateEvent = lazy(
  () => import('./views/CreateEventPage/CreateEventPage.tsx')
);

const EventView = lazy(() => import('./views/EventView/EventView.tsx'));
const PrivacyPolicy = lazy(
  () => import('./views/PrivacyPolicy/PrivacyPolicy.tsx')
);

const ClockPage = lazy(() => import('./views/ClockPage/ClockPage.tsx'));
const TokenTopUp = lazy(() => import('./views/TokenTopUp/TokenTopUp.tsx'));
const TokenSuccess = lazy(() => import('./views/TokenSuccess/TokenSuccess.tsx'));
const RSVPPage = lazy(() => import('./views/RSVPPage/RSVPPage.tsx'));
const TermsOfService = lazy(() => import('./views/TermsOfService/TermsOfService.tsx'));
const PassportPage = lazy(() => import('./views/Passport/PassportPage.tsx'));
const JoinPage = lazy(() => import('./views/JoinPage/JoinPage.tsx'));
const RulesCertification = lazy(() => import('./views/RulesCertification/RulesCertification.tsx'));
const PricingPage = lazy(() => import('./views/PricingPage/PricingPage.tsx'));
const HelpPage = lazy(() => import('./views/HelpPage/HelpPage.tsx'));
const BecomeTDPage = lazy(() => import('./views/BecomeTDPage/BecomeTDPage.tsx'));
const TokensPage = lazy(() => import('./views/TokensPage/TokensPage.tsx'));
const PageNotFound = lazy(
  () => import('./views/PageNotFound/PageNotFound.tsx')
);
function App() {
  return (
    <AuthContextProvider>
      <ChakraProvider theme={theme}>
          <Suspense fallback={<Fallback />}>
            <LoadScript
              googleMapsApiKey={`${process.env.REACT_APP_GOOGLE_API_KEY}`}
              libraries={['places'] as any[]}
              loadingElement={<></>}
              version='beta'
            >
            <Routes>
              <Route
                path='*'
                element={
                  <PageLayout>
                    <PageNotFound />
                  </PageLayout>
                }
              />
              <Route path='/ComingSoon' element={<ComingSoonPage />} />
              <Route
                path='/'
                element={
                  <LandingPageLayout>
                    <LandingPage />
                  </LandingPageLayout>
                }
              />
              <Route path='/dashboard' element={<PrivateRoute />}>
                <Route
                  path='/dashboard'
                  element={
                    <PageLayout hasSecondaryNav={true}>
                      <DashboardPage />
                    </PageLayout>
                  }
                />
              </Route>
              <Route path='/edit-event/:eventId' element={<PrivateRoute />}>
                <Route
                  path='/edit-event/:eventId'
                  element={
                    <PageLayout>
                      <CreateEvent />
                    </PageLayout>
                  }
                />
              </Route>
              <Route path='/create-event/' element={<PrivateRoute />}>
                <Route
                  path='/create-event/'
                  element={
                    <PageLayout>
                      <CreateEvent />
                    </PageLayout>
                  }
                />
              </Route>
              <Route path='/event/:eventId' element={<PrivateRoute />}>
                <Route
                  path='/event/:eventId'
                  element={
                    <PageLayout>
                      <EventView />
                    </PageLayout>
                  }
                />
              </Route>
              <Route
                path='/clock/:eventId' element={<PrivateRoute />}><Route path='/clock/:eventId' element={<PageLayout><ClockPage /></PageLayout>} /></Route>
              <Route path='/dealer-certification' element={<PrivateRoute />}><Route path='/dealer-certification' element={<PageLayout><DealerCertification /></PageLayout>} /></Route>
              <Route
                path='/top-up' element={<PrivateRoute />}><Route path='/top-up' element={<PageLayout><TokenTopUp /></PageLayout>} /></Route>
              <Route path='/live/:eventId' element={<LiveClockPage />} />
              <Route path='/dealer/:uid' element={<DealerPassportPage />} />
              <Route path='/find-a-dealer' element={<DealerDiscoveryPage />} />
              <Route path='/find-a-game' element={<FindAGamePage />} />
              <Route path='/submit-game' element={<PageLayout><SubmitGamePage /></PageLayout>} />
              <Route path='/pricing' element={<PageLayout><PricingPage /></PageLayout>} />
              <Route path='/help' element={<PageLayout><HelpPage /></PageLayout>} />
              <Route path='/become-td' element={<PrivateRoute />}><Route path='/become-td' element={<PageLayout><BecomeTDPage /></PageLayout>} /></Route>
              <Route path='/tokens' element={<PageLayout><TokensPage /></PageLayout>} />
              <Route path='/venue/:venueId' element={<VenueProfilePage />} />
              <Route path='/create-venue' element={<CreateVenuePage />} />
              <Route path='/venue/:venueId/apply' element={<PCTDSApplicationPage />} />
              <Route path='/room/:slug' element={<TDRoomPage />} />
              <Route path='/poster-generator' element={<PosterGeneratorPage />} />
              <Route path='/pctds-clock/:eventId' element={<PCTDSClockPage />} />
              <Route path='/pctds-clock' element={<PCTDSClockPage />} />
              <Route path='/td' element={<TDControlCenter />} />
              <Route
                path='/rsvp/:eventId'
                element={
                  <RSVPPage />
                }
              />
              <Route path='/join' element={<PageLayout><JoinPage /></PageLayout>} />
              <Route path='/certification' element={<PrivateRoute />}>
                <Route path='/certification' element={<PageLayout><RulesCertification /></PageLayout>} />
              </Route>
              <Route path='/passport/:uid' element={<PrivateRoute />}>
                <Route path='/passport/:uid' element={<PageLayout><PassportPage /></PageLayout>} />
              </Route>
              <Route
                path='/terms' element={<PageLayout><TermsOfService /></PageLayout>} />
              <Route path='token-success'
                element={
                  <PageLayout>
                    <TokenSuccess />
                  </PageLayout>
                }
              />
              <Route
                path='/privacy-policy'
                element={
                  <PageLayout>
                    <PrivacyPolicy />
                  </PageLayout>
                }
              />
            </Routes>
            </LoadScript>
          </Suspense>
      </ChakraProvider>
    </AuthContextProvider>
  );
}
export default App;
